import mapValues from 'lodash/mapValues';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import { useMemo } from 'react';
import { OwnedToken, TokenType } from 'components/common';
import { useWallet } from 'components/WalletContext';
import { useDropsContext } from 'components/Drops/DropsContext';
import useBalances from './useBalances';
import useOriginals from './useOriginals';

interface Grouped {
  [key: string]: OwnedToken[];
}

interface OwnedTokensMap {
  [key: string]: {
    [key: string]: {
      print?: number;
      original?: number;
    };
  };
}

function groupByContractAndReduce(
  grouped: Grouped,
  reduceFunc: (arg0: any, arg1: any) => any,
  init: any
) {
  return Object.keys(grouped).reduce((fullMap, contractAddress) => {
    const contractMap = grouped[contractAddress].reduce(reduceFunc, {});
    return {
      ...fullMap,
      [contractAddress]: {
        ...contractMap,
      },
    };
  }, {});
}

const useOwnedTokens = () => {
  const { address } = useWallet();
  const { dropConfig, selectedDrop } = useDropsContext();

  const { all } = useOriginals();
  const { balances, status } = useBalances(selectedDrop, address);

  // note - this just recreates ownedTokens endpoint. Could refactor this file
  // to just get what is needed
  const ownedTokens: OwnedToken[] = useMemo(() => {
    if (!balances) return [];

    let owned: OwnedToken[] = [];

    all.forEach(o => {
      // check if own original
      const originalBalance = balances[o.id];
      const printsBalance = balances[o!.print!.id];

      if (originalBalance > 0) {
        owned.push({
          contractAddress: o.contractAddress,
          tokenId: o.id,
          balance: originalBalance,
          type: TokenType.original,
          original: o,
        });
      }

      if (printsBalance > 0) {
        owned.push({
          contractAddress: o.contractAddress,
          tokenId: o!.print!.id,
          balance: printsBalance,
          type: TokenType.print,
          original: o,
        });
      }
    });

    return owned;
  }, [balances, all]);

  const defaultSort = useMemo(() => {
    return orderBy(ownedTokens, 'mintNumber');
  }, [ownedTokens]);

  const groupedByContract = useMemo(() => groupBy(defaultSort, 'contractAddress'), [defaultSort]);

  // contract => original.id => print and original balances
  const ownedTokensMap: OwnedTokensMap = useMemo(() => {
    return groupByContractAndReduce(
      groupedByContract,
      (map, owned) => {
        return {
          ...map,
          [owned.original.id]: {
            ...map[owned.original.id],
            [owned.type]: owned.balance,
          },
        };
      },
      {}
    );
  }, [groupedByContract]);

  // array of Originals owned (unique by original)
  const ownedPlaylist = useMemo(() => {
    return mapValues(groupedByContract, (ownedTokens: OwnedToken[]) => {
      const uniqOriginals = uniqBy(ownedTokens, t => t.original.id);
      return uniqOriginals.map(o => o.original);
    });
  }, [groupedByContract]);

  return {
    groupedByContract,
    all: defaultSort || [],
    ownedTokensMap,
    ownedPlaylist,
    activeDropOwnedPlaylist: ownedPlaylist[dropConfig.contractAddress],
    address,
    status,
  };
};

export default useOwnedTokens;
